// Única capa que conoce el backend SQLite (STOR-05). Toda escritura SQL pasa por aquí.
//
// Facade delgado y UNIFORME sobre un backend intercambiable: en web se selecciona el motor WASM más
// fuerte (opfs-sahpool -> wa-sqlite-idb) y en nativo se usa @capacitor-community/sqlite; AMBOS implementan
// el mismo contrato (open/query/run/execute/executeSet/transaction/persist/reopen/close). Nada por
// encima (repositorios, sync engine, módulos) conoce el motor: ven solo
// query/run/executeSet/execute/transaction + open/close/persist + installLifecycleHandlers/enableEncryption.
//
// La orquestación que vive AQUÍ (write-mutex, proxyHandle de tablas tocadas, notifyMany post-commit,
// migración por PRAGMA user_version) es independiente del motor: un solo camino para web y nativo.

import { computed, shallowRef } from 'vue'
import { App } from '@capacitor/app'
import { selectWebBackend } from './web/selectBackend'
import { NativeCapacitorSqliteBackend } from './native/NativeCapacitorSqliteBackend'
import { runUserVersionMigration } from './runUserVersionMigration'
import { requestPersistentStorage } from './web/persistStorage'
import { isWebPlatform, isNativePlatform, nativeReady } from './platform'
import { notifyMany } from '../reactivity/change-bus'
import { dbg, dbgError } from '../logger'

// ─── Emit de reactividad (UI-01): CAPA UNICA DE EMISION ────────────────────────────────────────
// El StorageAdapter es el unico punto que dispara el change-bus (Pitfall 4: double-emit). Toda
// escritura (optimistic, pull upsert, applyServerRows, outbox flips, success-write, purge) pasa por
// aqui, asi que emitir aqui cubre el 100% sin tocar repos/sync/domain.
//
// REGLAS (no romper):
//   - POST-COMMIT SOLO: el notifyMany corre DESPUES de que la escritura/tx resuelve. NUNCA dentro de
//     una tx abierta — un ROLLBACK dejaria a la UI re-consultando datos que no se commitearon
//     (Pitfall 2: rollback hazard). El path de error NO emite (el throw salta el notify).
//   - UN emit por tabla por tx (Pitfall 1): en transaction(fn) se RECOLECTAN las tablas tocadas por
//     todos los run(...,false) internos via un proxy del handle, y se emite UNA vez por tabla al
//     commit. El pull de 27 chunks en una sola tx = 1 emit de c_locations, no 27.

// Extrae los nombres de tabla destino de un SQL de ESCRITURA (INSERT/UPDATE/DELETE/REPLACE).
// Ignora SELECT/PRAGMA/BEGIN/COMMIT (lecturas/control = sin emit). Devuelve array de-duplicado.
const WRITE_TABLE_PATTERNS = [
  /\bINSERT\s+(?:OR\s+\w+\s+)?INTO\s+"?([A-Za-z_][A-Za-z0-9_]*)"?/gi,
  /\bUPDATE\s+"?([A-Za-z_][A-Za-z0-9_]*)"?/gi,
  /\bDELETE\s+FROM\s+"?([A-Za-z_][A-Za-z0-9_]*)"?/gi,
  /\bREPLACE\s+INTO\s+"?([A-Za-z_][A-Za-z0-9_]*)"?/gi
]

function tablesFromSql (sql, into) {
  const set = into || new Set()
  if (!sql) return [...set]
  for (const re of WRITE_TABLE_PATTERNS) {
    re.lastIndex = 0
    let m
    while ((m = re.exec(sql)) !== null) set.add(m[1])
  }
  return [...set]
}

function tablesFromSet (set) {
  const acc = new Set()
  for (const s of (set || [])) tablesFromSql(s && s.statement, acc)
  return [...acc]
}

// Proxy del handle de una tx: registra las tablas de cada run() en `touched`, delega todo lo demas
// (query, etc.) al handle real sin alterar comportamiento (mismas firmas y valores de retorno).
function proxyHandle (handle, touched) {
  return new Proxy(handle, {
    get (target, prop, receiver) {
      if (prop === 'run') {
        return function (sql, params, txn) {
          tablesFromSql(sql, touched)
          return target.run(sql, params, txn)
        }
      }
      const value = Reflect.get(target, prop, receiver)
      return typeof value === 'function' ? value.bind(target) : value
    }
  })
}

class StorageAdapterClass {
  constructor () {
    // Backend SQLite ACTIVO: web (opfs-sahpool|wa-sqlite-idb) o nativo (@capacitor-community/sqlite).
    // Mismo contrato para ambos -> un solo camino de delegación.
    //
    // DISPONIBILIDAD REACTIVA: el backend vive en un shallowRef y `_backend` es un ACCESSOR sobre él
    // (get/set en el prototipo). Así TODA asignación que ya existía (_doOpen publica, close() limpia,
    // los tests ligan su shim) actualiza la reactividad sin tocar una sola línea de esos caminos, y
    // `isReady()` sigue siendo exactamente la misma lectura puntual. La versión reactiva para la UI es
    // el `storageReady` exportado al final del archivo.
    this._backendRef = shallowRef(null)
    this._opening = null // promesa de apertura EN VUELO (coalesce de open() concurrentes)
    this._lifecycleInstalled = false
    this._lastOpenOpts = { encrypted: false, mode: 'no-encryption' }
    // WRITE-MUTEX (fix interleaving race): SQLite es single-writer. run/executeSet/execute/transaction
    // emiten BEGIN/fn/COMMIT como mensajes await-eados SEPARADOS sobre UNA conexion compartida; sin
    // exclusion mutua, una escritura concurrente disparada en un await-point puede aterrizar DENTRO de
    // una tx abierta (se une a ella; el rollback la mata; o un BEGIN anidado lanza). Este lock serializa
    // TODA escritura (las lecturas via query() siguen SIN lock). Es una cadena de promesas: cada
    // adquisicion encola al final; cada liberacion (en finally) resuelve la siguiente.
    this._writeLock = Promise.resolve()
    this._openedListeners = new Set()
  }

  /**
   * Avisa cada vez que una apertura o reapertura termina bien (open() publica el backend o reopenIfClosed
   * lo reabre). El boot lo usa para completar los pasos que no corrió si la base no abrió al arrancar.
   * @param {function} listener
   * @returns {function} quita el listener.
   */
  onOpened (listener) {
    this._openedListeners.add(listener)
    return () => this._openedListeners.delete(listener)
  }

  _notifyOpened () {
    for (const listener of [...this._openedListeners]) {
      try {
        Promise.resolve(listener()).catch(error => dbgError('lifecycle', 'listener de apertura falló', error))
      } catch (error) {
        dbgError('lifecycle', 'listener de apertura falló', error)
      }
    }
  }

  /** Backend activo (lectura). Delega en el shallowRef -> la lectura queda TRACKEADA por Vue. */
  get _backend () {
    return this._backendRef.value
  }

  /** Publica/limpia el backend activo. Delega en el shallowRef -> notifica a la UI reactiva. */
  set _backend (backend) {
    this._backendRef.value = backend
  }

  /**
   * Serializa una seccion critica de ESCRITURA encadenandola a la cola del _writeLock. La cadena se
   * avanza SIEMPRE (resolve en finally) aunque `fn` lance, para que un error no congele la cola.
   * @param {function} fn  thunk async con la escritura real.
   * @returns {Promise<*>}
   */
  _withWriteLock (fn) {
    const run = this._writeLock.then(fn, fn) // espera al anterior (resuelva o no), luego corre fn.
    // Avanza la cola SIN propagar el error de fn (el catch vacio solo sirve para no romper la cadena;
    // el resultado/rechazo real de fn lo recibe el caller via `run`).
    this._writeLock = run.then(() => {}, () => {})
    return run
  }

  /**
   * Web only: NO-OP. La inicialización del motor la hace el backend seleccionado en su open().
   * Se conserva por compatibilidad con cualquier llamador histórico.
   */
  async initWeb () {
    // intencionalmente vacío.
  }

  /**
   * Secuencia de apertura UNIFORME (web y nativo):
   *   1. (nativo) await a `nativeReady`, hoy un no-op: bajo Capacitor el bridge y los
   *      plugins ya están listos cuando corre el JS (no hay evento tipo 'deviceready' que esperar).
   *   2. seleccionar/crear el backend e abrirlo (pragmas de durabilidad dura dentro del open nativo).
   *   3. correr la migración de esquema por PRAGMA user_version (misma escalera para ambos motores).
   *
   * `encrypted`/`mode` se hilan al backend pero por DEFECTO van apagados (SEC-01 es v2).
   */
  async open (opts) {
    // COALESCE de aperturas concurrentes: instalar los lifecycle handlers ANTES del open() (self-heal del
    // boot-race) abre una ventana donde un resume/visibilitychange durante el await del open inicial podría
    // disparar reopenIfClosed -> this.open() en paralelo. Devolver la MISMA promesa en vuelo evita doble
    // backend / doble migración / conexión filtrada. Se limpia en finally para permitir reintentos futuros.
    if (this._opening) return this._opening
    this._opening = this._doOpen(opts).finally(() => { this._opening = null })
    return this._opening
  }

  async _doOpen ({ encrypted = false, mode = 'no-encryption' } = {}) {
    this._lastOpenOpts = { encrypted, mode }

    let backend
    let alreadyOpen = false
    if (isNativePlatform()) {
      await nativeReady
      backend = new NativeCapacitorSqliteBackend()
    } else {
      // Web: selectWebBackend ABRE y devuelve el motor ya abierto (opfs-sahpool -> wa-sqlite-idb, con
      // fallthrough si el open de un tier falla). El open real ES la selección: no re-abrir.
      backend = await selectWebBackend({ encrypted, mode })
      alreadyOpen = true
    }
    dbg('storage', `backend seleccionado: ${backend.name}`)

    if (!alreadyOpen) await backend.open({ encrypted, mode })
    // Migración de esquema versionada (V1/V2/V3) — una sola fuente lógica para web y nativo.
    await runUserVersionMigration(backend)
    dbg('storage', 'open + migración user_version OK')
    // PUBLICAR this._backend SOLO tras open()+migración OK. Si cualquiera lanza (p.ej. plugin nativo aún no
    // inyectado en un cold start lento), this._backend queda en null -> reopenIfClosed reintenta por
    // this.open() (camino COMPLETO con migración), NO por backend.reopen() (que no migra y, en instalación
    // nueva, dejaría la DB abierta SIN esquema). Así las capturas se recuperan enteras (conexión + esquema).
    this._backend = backend
    this._notifyOpened()
    return this._backend
  }

  /**
   * ¿Hay un backend SQLite ABIERTO y usable AHORA MISMO? Solo lectura, sin efectos secundarios: no
   * abre, no reabre, no toca el write-lock. Refleja el invariante de _doOpen (this._backend se
   * publica SOLO tras open()+migración OK), así que `false` significa que la base NO abrió o quedó
   * cerrada, y toda escritura contra ella lanzaría.
   *
   * Existe para que la UI pueda DECIDIR el camino antes de intentar la escritura: si devuelve false,
   * el llamador debe degradar al flujo online de siempre en vez de encolar en SQLite y fallar. Casos
   * reales en que da false: el plugin nativo pierde su llave interna en el keystore del sistema, un
   * cold start donde el bridge aún no inyectó el plugin, o una conexión cerrada tras background.
   *
   * NO es un chequeo de salud del motor: dice que la conexión existe, no que la próxima query vaya a
   * responder. Tampoco reemplaza el try/catch alrededor de la escritura.
   * @returns {boolean}
   */
  isReady () {
    return !!this._backend
  }

  /**
   * Lecturas + PRAGMAs que devuelven valor. Serializadas por el _writeLock igual que las escrituras.
   *
   * wa-sqlite async (Asyncify) NO es reentrante: una lectura que corre MIENTRAS una escritura (o cualquier
   * otra llamada al WASM) sigue en vuelo corrompe el heap del módulo -> `RuntimeError: memory access out of
   * bounds` en `_sqlite3_malloc`/`exec` (visto en device y web: drain que LEE justo cuando el usuario teclea
   * un comentario y `addAddon` ESCRIBE). Antes las lecturas bypasseaban el lock y esa colisión reventaba la
   * conexión para el resto de la sesión. El _writeLock ya no es "solo escritura": serializa todo el ACCESO
   * NORMAL (query/run/executeSet/execute/transaction) a la única conexión, que es lo que un motor
   * no-reentrante exige. (Las ops de LIFECYCLE — open/reopen/close/persist — quedan fuera del lock a
   * propósito; corren en boot/pagehide/resume, no compiten con el tráfico normal de datos.)
   *
   * SIN deadlock: las lecturas DENTRO de una transacción usan el HANDLE (db.query/h.query), NO esta global,
   * así que tomar el lock aquí nunca se re-entra desde una sección ya bloqueada (auditado: ningún callback de
   * transaction/run llama a StorageAdapter.query global).
   */
  query (sql, params) {
    return this._withWriteLock(() => this._backend.query(sql, params))
  }

  /** Escritura parametrizada simple. Serializada por el _writeLock. Emite notifyMany POST-resolve. */
  async run (sql, params, transaction = true) {
    const r = await this._withWriteLock(() => this._backend.run(sql, params, transaction))
    notifyMany(tablesFromSql(sql)) // POST-commit (UI-01). El throw saltaria esta linea.
    return r
  }

  /**
   * Batch atomico (fila de datos + fila de outbox juntas): el camino cero-perdida que usa la Fase 3.
   * `set` = [{ statement, values }]. Serializada por el _writeLock. Emite notifyMany POST-commit.
   */
  async executeSet (set, transaction = true) {
    const r = await this._withWriteLock(() => this._backend.executeSet(set, transaction))
    notifyMany(tablesFromSet(set)) // POST-commit (UI-01).
    return r
  }

  /** DDL / multi-statement + PRAGMAs sin valor de retorno. Serializada por el _writeLock. */
  async execute (statements, transaction = true) {
    const r = await this._withWriteLock(() => this._backend.execute(statements, transaction))
    notifyMany(tablesFromSql(statements)) // DDL emite a 0 listeners = inocuo (UI-01).
    return r
  }

  /**
   * Envuelve un bloque multi-paso en una transaccion explicita con rollback en caso de error
   * (lo provee el backend.transaction). Las tablas tocadas por los run(...,false) internos se
   * recolectan via un PROXY del handle y se emiten UNA vez por tabla AL COMMIT (Pitfall 1). En error
   * -> rollback -> NO emite (el throw salta el notifyMany, Pitfall 2). El backend.transaction(fn)
   * resuelve TRAS commit, asi que el emit tras el await es post-commit. Los run internos van al handle
   * REAL (no a este .run), por lo que el emit de .run() de arriba NO se duplica (Pitfall 4).
   */
  async transaction (fn) {
    const touched = new Set()
    // WRITE-MUTEX: TODO el BEGIN/fn/COMMIT corre dentro del _writeLock, asi ninguna run/executeSet/
    // execute concurrente puede aterrizar DENTRO de esta tx abierta. Los run(...,false) INTERNOS van al
    // handle REAL via proxyHandle (this._backend), NO a este StorageAdapter.run -> NO re-toman el lock.
    const result = await this._withWriteLock(() =>
      this._backend.transaction((h) => fn(proxyHandle(h, touched)))
    )
    notifyMany(touched) // POST-commit (el backend.transaction resolvio = commit).
    return result
  }

  /**
   * Seam forward-compat INTENCIONALMENTE no-op HOY [review Hugo #14]: los 3 backends actuales
   * (OPFS-sahpool, wa-sqlite-idb, cordova-sqlite nativo) escriben AL VUELO — OPFS/wa-sqlite al
   * filesystem/IndexedDB y el nativo al filesystem con el WAL ya commiteado — así que no hay nada que
   * volcar. Existe para que un futuro backend EN MEMORIA pueda enganchar aquí su flush sin re-cablear
   * los lifecycle handlers. NO promete durabilidad (la red de cero-pérdida es el sync del outbox al
   * servidor). El try/catch garantiza que NUNCA revienta en pagehide/visibilitychange (esos handlers
   * no pueden propagar errores). (Sonar S1186 "empty async method" en los backends = falso positivo.)
   */
  async persist () {
    try {
      await this._backend?.persist?.()
    } catch (error) {
      console.warn('[offline-v2] persist() fallo (no fatal):', String(error && error.message ? error.message : error))
    }
  }

  /**
   * Web only (WDUR-04): solicita almacenamiento persistente al navegador UNA vez. En nativo no aplica
   * (el sandbox de datos ya es persistente). NO es garantia de durabilidad — la red de cero-perdida es
   * el sync del outbox al servidor.
   * @returns {Promise<boolean>}
   */
  async requestPersistence () {
    if (!isWebPlatform()) return false
    return requestPersistentStorage()
  }

  /** Cierra la conexion del backend activo. */
  async close () {
    if (this._backend) await this._backend.close()
    this._backend = null
  }

  /**
   * Reabre la conexion si quedo cerrada tras background/resume. Si el backend se perdio del todo,
   * re-abre via open() (re-selecciona el motor); si sigue vivo, delega en backend.reopen().
   */
  async reopenIfClosed () {
    try {
      if (!this._backend) {
        dbg('lifecycle', 'reopenIfClosed: sin backend -> open() completo (re-selecciona motor + migra)')
        await this.open(this._lastOpenOpts)
        dbg('lifecycle', 'reopenIfClosed: recuperación por open() OK')
        return
      }
      await this._backend.reopen?.()
      this._notifyOpened()
    } catch (error) {
      dbgError('lifecycle', 'reopenIfClosed falló', error)
    }
  }

  /**
   * Nativo only (SEC-01, v2 — seam): delega en el backend si soporta cifrado. En web es no-op.
   */
  async enableEncryption (passphrase) {
    if (isWebPlatform()) return
    await this._backend?.enableEncryption?.(passphrase)
  }

  /**
   * STOR-06: engancha el seam persist() en pagehide y visibilitychange=hidden (síncronos del WebView,
   * sirven en web Y en nativo). Ademas reabre la conexion al volver a primer plano. En nativo se
   * añade appStateChange de @capacitor/app (resume/pause del dispositivo).
   * Hoy la durabilidad NO depende de estos handlers: los 3 backends escriben al vuelo (ver persist()).
   */
  installLifecycleHandlers () {
    if (this._lifecycleInstalled) return
    this._lifecycleInstalled = true

    // Flush síncrono del WebView (web y nativo).
    window.addEventListener('pagehide', () => { dbg('lifecycle', 'pagehide -> persist'); this.persist() })
    document.addEventListener('visibilitychange', () => {
      if (document.visibilityState === 'hidden') { dbg('lifecycle', 'hidden -> persist'); this.persist() }
      else if (document.visibilityState === 'visible') { dbg('lifecycle', 'visible -> reopenIfClosed'); this.reopenIfClosed() }
    })

    // Nativo: reabrir al volver de background (la WebView puede cerrar la conexión).
    if (isNativePlatform()) {
      App.addListener('appStateChange', ({ isActive }) => {
        if (isActive) {
          dbg('lifecycle', 'resume -> reopenIfClosed')
          this.reopenIfClosed()
        } else {
          dbg('lifecycle', 'pause -> persist')
          this.persist()
        }
      })
    }
  }
}

export const StorageAdapter = new StorageAdapterClass()

/**
 * Version REACTIVA de `isReady()`: mismo valor, pero como computed de Vue, para que un `computed` de
 * componente (o cualquier efecto) se RE-EVALUE solo cuando la base abre o se cierra. `isReady()` es una
 * lectura puntual: metida tal cual en un computed, se congelaria en el valor del primer render y la UI
 * seguiria mostrando el estado viejo cuando la base se reabre (o cuando se cae).
 *
 * Uso tipico (Options API): `computed: { isStorageReady () { return storageReady.value } }`.
 * Sigue SIN efectos secundarios: no abre, no reabre, no toca el write-lock.
 */
export const storageReady = computed(() => StorageAdapter.isReady())
